import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { referencesAjax } from "../../ajax/ReferencesAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { ShiftDTO } from "../../DTOs/ShiftDTO";
import { setSelectedDate, setSelectedShift, setLatestShift } from "../../state/reducers/shiftReducer";
import useShiftTimes from "../Hooks/useShiftTimes";
import { refreshLatestShift } from "../../offline/refreshLatestShift";

const STORAGE_DATE = "shiftDate";
const STORAGE_DON = "shiftDayOrNight";
const STORAGE_FALLBACK_KEY = "mw_presence_ping";
const SESSION_FETCH_FLAG = "shiftFetchedThisSession";
const SESSION_JOINED_FLAG = "shiftJoinedExistingSession";

export default function ShiftLoginBootstrapper() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((s: RootState) => s.authReducer.isAuthenticated);
  const rehydrated = useSelector((s: RootState) => s?._persist?.rehydrated === true);
  const { isTimezoneLoading } = useShiftTimes();

  const prevAuthRef = useRef<boolean | null>(null);
  const inFlightRef = useRef(false);
  const someoneElseRef = useRef(false);
  const [presenceReady, setPresenceReady] = useState(false);

  const fetchLatest = async () => {
    try {
      const raw = await referencesAjax.getLatestShift();
      if (!(raw instanceof ErrorResponse) && ShiftDTO.is(raw)) {
        let iso: string | null = null;
        try {
          if (typeof raw.date === "string") iso = raw.date;
          else if (raw.date?.toISODate) iso = raw.date.toISODate();
        } catch {
        }
        if (iso) {
          dispatch(setSelectedDate(iso));
          sessionStorage.setItem(STORAGE_DATE, iso);
        }
        if (raw.dayOrNight) {
          const code = raw.dayOrNight === "D" ? "DAY" : "NIGHT";
          dispatch(setSelectedShift(code));
          sessionStorage.setItem(STORAGE_DON, raw.dayOrNight);
          if (iso) dispatch(setLatestShift({ date: iso, shift: code }));
        }
        sessionStorage.setItem(SESSION_FETCH_FLAG, "1");
      }
    } catch {
    }
  };

  useEffect(() => {
    let t: any;
    if ("BroadcastChannel" in globalThis) {
      const bc = new BroadcastChannel("mw_presence");
      bc.onmessage = (e: MessageEvent) => {
        const msg = e.data;
        if (msg === "pong") {
          someoneElseRef.current = true;
          sessionStorage.setItem(SESSION_JOINED_FLAG, "1");
        }
        if (msg === "ping") bc.postMessage("pong");
      };
      bc.postMessage("ping");
      t = setTimeout(() => setPresenceReady(true), 200);
      return () => {
        clearTimeout(t);
        bc.close();
      };
    } else {
      const handler = (ev: StorageEvent) => {
        if (ev.key !== STORAGE_FALLBACK_KEY) return;
        if (ev.newValue === "ping") localStorage.setItem(STORAGE_FALLBACK_KEY, "pong");
        if (ev.newValue === "pong") {
          someoneElseRef.current = true;
          sessionStorage.setItem(SESSION_JOINED_FLAG, "1");
        }
      };
      const w = globalThis as Window & typeof globalThis;
      w.addEventListener("storage", handler);
      localStorage.setItem(STORAGE_FALLBACK_KEY, "ping");
      t = setTimeout(() => setPresenceReady(true), 200);
      return () => {
        clearTimeout(t);
        w.removeEventListener("storage", handler);
      };
    }
  }, []);

  // Note: fetchLatest runs once per session and also moves the user's selection, so the latest-shift
  // marker never advanced while a session stayed open. Refresh just the marker whenever we are online
  // - it decides which shift is editable offline and which one may be completed offline, so a stale
  // value locks the crew out of the shift they are actually working.
  useEffect(() => {
    if (!isAuthenticated) return;

    refreshLatestShift();
    const onReconnect = () => { refreshLatestShift(); };
    window.addEventListener("online", onReconnect);
    return () => window.removeEventListener("online", onReconnect);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!rehydrated || !presenceReady || isTimezoneLoading) return;

    const prev = prevAuthRef.current;
    const fetchedThisSession = sessionStorage.getItem(SESSION_FETCH_FLAG) === "1";
    const joinedExisting = sessionStorage.getItem(SESSION_JOINED_FLAG) === "1";

    if (!isAuthenticated) {
      sessionStorage.removeItem(SESSION_FETCH_FLAG);
      sessionStorage.removeItem(SESSION_JOINED_FLAG);
      prevAuthRef.current = isAuthenticated;
      return;
    }

    if (fetchedThisSession) {
      prevAuthRef.current = isAuthenticated;
      return;
    }

    const justLoggedIn = prev === false && isAuthenticated === true;
    if (justLoggedIn && !inFlightRef.current) {
      inFlightRef.current = true;
      fetchLatest().finally(() => {
        inFlightRef.current = false;
      });
      prevAuthRef.current = isAuthenticated;
      return;
    }

    const firstTabAlreadyAuthed = prev === null && isAuthenticated === true && !someoneElseRef.current;

    if (firstTabAlreadyAuthed && !inFlightRef.current) {
      if (joinedExisting) {
        sessionStorage.setItem(SESSION_FETCH_FLAG, "1");
        prevAuthRef.current = isAuthenticated;
        return;
      }
      inFlightRef.current = true;
      fetchLatest().finally(() => {
        inFlightRef.current = false;
      });
      prevAuthRef.current = isAuthenticated;
      return;
    }

    prevAuthRef.current = isAuthenticated;
  }, [rehydrated, presenceReady, isTimezoneLoading, isAuthenticated, dispatch]);

  return null;
}
